import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { TrendingDown, Users, Lock, UtensilsCrossed } from "lucide-react";
import Footer from "~/components/Footer";
import styles from "~/styles/business-tools.module.css";

export const meta: MetaFunction = () => [
  { title: "Business Tools — Prodigium Dynamics" },
  { name: "description", content: "Precision-built tools for founders, operators, and builders. No subscriptions. No bloat." },
];

const TOOLS = [
  {
    name: "Burn Rate",
    desc: "Cash runway & burn rate calculator",
    tag: "Finance",
    status: "Live" as const,
    href: "/burn-rate",
    locked: false,
    Icon: TrendingDown,
  },
  {
    name: "Prodi-Food",
    desc: "Contract signing & restaurant management beta",
    tag: "Restaurants",
    status: "Live" as const,
    href: "/prodi-food",
    locked: false,
    Icon: UtensilsCrossed,
  },
  {
    name: "SoloBizCRM",
    desc: "Simple, mobile-first CRM for independent professionals",
    tag: "CRM",
    status: "Soon" as const,
    href: null,
    locked: false,
    Icon: Users,
  },
  { name: null, desc: null, tag: null, status: "Soon" as const, href: null, locked: true, Icon: Lock },
  { name: null, desc: null, tag: null, status: "Soon" as const, href: null, locked: true, Icon: Lock },
];

export default function BusinessToolsPage() {
  return (
    <>
      <div className={styles.pageWrap}>

        {/* ── SIGNAL PANEL ────────────────────────────────── */}
        <div className={styles.signalPanel}>
          <div className={styles.signalCore}>
            <div className={styles.signalDot} />
            <div className={styles.signalRing} />
            <div className={`${styles.signalRing} ${styles.r2}`} />
            <div className={`${styles.signalRing} ${styles.r3}`} />
            <div className={`${styles.signalRing} ${styles.r4}`} />
          </div>
          <p className={styles.signalLabel}>Tools</p>
          <h2 className={styles.signalStatus}>
            Built to<br /><span>Scale.</span>
          </h2>
          <p className={styles.signalDesc}>
            Precision-engineered tools for founders and operators.
            No subscriptions. No bloat. Just results.
          </p>
        </div>

        {/* ── TOOLS PANEL ─────────────────────────────────── */}
        <div className={styles.toolsPanel}>
          <p className={styles.toolsEyebrow}>Business Tools</p>
          <h1 className={styles.toolsTitle}>By Prodigium<br />Dynamics.</h1>
          <p className={styles.toolsStatement}>
            Every tool in this suite is built in production, not theory.
            Tested by the people who build with them every day.
          </p>

          <div className={styles.badge}>
            <span className={styles.badgeDot} />
            <span>Precision-built. No bloat.</span>
          </div>

          <div className={styles.toolsList}>
            {TOOLS.map(({ name, desc, tag, status, href, locked, Icon }, i) => {
              if (locked) {
                return (
                  <div key={i} className={`${styles.toolRow} ${styles.toolRowLocked}`}>
                    <div className={styles.toolRowLeft}>
                      <span className={styles.toolRowIcon}>
                        <Icon size={16} strokeWidth={1.4} />
                      </span>
                      <div className={styles.toolRowMeta}>
                        <span className={styles.toolRowName}>Classified</span>
                        <span className={styles.toolRowDesc}>Undisclosed</span>
                      </div>
                    </div>
                    <div className={styles.toolRowRight}>
                      <span className={styles.toolRowStatus}>— Soon</span>
                    </div>
                  </div>
                );
              }

              const inner = (
                <>
                  <div className={styles.toolRowLeft}>
                    <span className={styles.toolRowIcon}>
                      <Icon size={16} strokeWidth={1.4} />
                    </span>
                    <div className={styles.toolRowMeta}>
                      <span className={styles.toolRowName}>{name}</span>
                      <span className={styles.toolRowDesc}>{desc}</span>
                    </div>
                  </div>
                  <div className={styles.toolRowRight}>
                    <span className={styles.toolRowTag}>{tag}</span>
                    <span className={`${styles.toolRowStatus} ${status === "Live" ? styles.statusLive : ""}`}>
                      {status === "Live" && <span className={styles.statusDot} />}
                      {status === "Live" ? "Live →" : "— Soon"}
                    </span>
                  </div>
                </>
              );

              return href ? (
                <Link key={name} to={href} className={styles.toolRow}>
                  {inner}
                </Link>
              ) : (
                <div key={name} className={`${styles.toolRow} ${styles.toolRowSoon}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
}
